import type {
  CurriculumCore,
  CurriculumCoreItem,
  CurriculumOpenStatus,
  TrieKey,
} from '@mathflat/domain/Curriculum'
import type { Revision, SchoolType } from '@mathflat/domain/SchoolSystem'
import { makeAutoObservable, observable } from 'mobx'

/** 학년·학기·과목 드롭다운의 "전체" 선택지 */
type AllTypeCurriculumCoreItem = {
  isAllType: true
  trieKey: 'ALL'
  upperTrieKey: 'ALL'
  lowerTrieKeys: ['ALL']
  revision: undefined
  schoolType: SchoolType | null
  gradeSubject: undefined
  semester: null
  categorizedGrade: undefined
  status: CurriculumOpenStatus
  label: string
  labelShort: string
  labelWithoutSchoolType: string
  labelWithSpace: string
}

type Params = {
  includeAll?: boolean
  expandedLabel?: string
  includePreOpen?: boolean
  revisions?: Revision[]
}

const defaultParams = {
  includeAll: true,
  includePreOpen: true,
  revisions: ['CURRICULUM_15', 'CURRICULUM_22'],
} as const satisfies Params

export class CurriculumService {
  protected _curriculumMap: Map<TrieKey, CurriculumCoreItem> = new Map()

  constructor(curriculum: CurriculumCore) {
    makeAutoObservable<CurriculumService, '_curriculumMap'>(this, {
      _curriculumMap: observable.shallow,
    })

    const { grades, semesters, subjects } = curriculum

    grades.forEach((grade) => {
      this._curriculumMap.set(grade.trieKey, grade)
    })

    semesters.forEach((semester) => {
      this._curriculumMap.set(semester.trieKey, semester)
    })

    subjects.forEach((subject) => {
      this._curriculumMap.set(subject.trieKey, subject)
    })
  }

  private get _gradeSubjects(): Record<SchoolType, CurriculumCoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && !item.semester && item.status !== 'HIDDEN',
      ),
      MIDDLE: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && !item.semester && item.status !== 'HIDDEN',
      ),
      HIGH: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH' && item.status !== 'HIDDEN',
      ),
    }
  }

  get allGrade() {
    return [
      ...this._gradeSubjects.ELEMENTARY,
      ...this._gradeSubjects.MIDDLE,
      ...this._gradeSubjects.HIGH,
    ]
  }

  private get _gradeSemesterSubjects(): Record<SchoolType, CurriculumCoreItem[]> {
    return {
      ELEMENTARY: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'ELEMENTARY' && item.semester && item.status !== 'HIDDEN',
      ),
      MIDDLE: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'MIDDLE' && item.semester && item.status !== 'HIDDEN',
      ),
      HIGH: Array.from(this._curriculumMap.values()).filter(
        (item) => item.schoolType === 'HIGH' && item.status !== 'HIDDEN',
      ),
    }
  }

  private _filterByRevision = (targetRevision: Revision, revisions: Revision[]) => {
    return revisions.includes(targetRevision)
  }

  private _filterByPreOpenInclude = (includePreOpen: boolean, item: CurriculumCoreItem) => {
    if (includePreOpen) return true
    return item.status !== 'PRE_OPEN'
  }

  private _getAllTypeItem({
    schoolType,
    expandedLabel,
  }: {
    schoolType?: SchoolType
    expandedLabel?: string
  }): AllTypeCurriculumCoreItem | undefined {
    const label = expandedLabel || (schoolType === 'HIGH' ? '과목 전체' : '학년·학기')
    return {
      isAllType: true,
      trieKey: 'ALL',
      upperTrieKey: 'ALL',
      lowerTrieKeys: ['ALL'],
      revision: undefined,
      schoolType: schoolType || null,
      gradeSubject: undefined,
      semester: null,
      categorizedGrade: undefined,
      status: 'ACTIVE',
      label,
      labelShort: label,
      labelWithoutSchoolType: label,
      labelWithSpace: label,
    }
  }

  private _getCurriculumArray({
    type = 'GRADE',
    includeAll = defaultParams.includeAll,
    includePreOpen = defaultParams.includePreOpen,
    revisions = defaultParams.revisions,
    expandedLabel,
    schoolType = 'ELEMENTARY',
  }: Params & { schoolType: SchoolType; type: 'GRADE' | 'SEMESTER' }) {
    let _gradeSubject: CurriculumCoreItem[] = []
    switch (schoolType) {
      case 'ELEMENTARY':
        _gradeSubject =
          type === 'SEMESTER'
            ? this._gradeSemesterSubjects.ELEMENTARY
            : this._gradeSubjects.ELEMENTARY
        break
      case 'MIDDLE':
        _gradeSubject =
          type === 'SEMESTER' ? this._gradeSemesterSubjects.MIDDLE : this._gradeSubjects.MIDDLE
        break
      case 'HIGH':
        _gradeSubject = this._gradeSubjects.HIGH
        break
    }

    _gradeSubject =
      _gradeSubject
        .filter((item) => this._filterByRevision(item.revision || 'CURRICULUM_15', revisions))
        .filter((item) => this._filterByPreOpenInclude(includePreOpen, item)) || []

    if (includeAll) {
      const ALL_TYPE_ITEM = this._getAllTypeItem({ schoolType, expandedLabel })
      return ALL_TYPE_ITEM ? [ALL_TYPE_ITEM, ..._gradeSubject] : _gradeSubject
    } else {
      return _gradeSubject
    }
  }
  get allGradeSemester() {
    return [
      ...this.getGradeSemesterElementary({ includeAll: false }),
      ...this.getGradeSemesterMiddle({ includeAll: false }),
      ...this.getGradeSubjectHigh({ includeAll: false }),
    ]
  }
  // 초등학교 ======================================================================================================
  /** 초등학교 학년-학기 */
  getGradeSemesterElementary<T extends boolean>(
    params: Params & { includeAll?: T } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'ELEMENTARY',
      type: 'SEMESTER',
    }) as T extends true
      ? (AllTypeCurriculumCoreItem | CurriculumCoreItem<'ELEMENTARY'>)[]
      : CurriculumCoreItem<'ELEMENTARY'>[]
  }

  // 중학교 ======================================================================================================
  /** 중학교 학년 - 학기 */
  getGradeSemesterMiddle<T extends boolean>(
    params: Params & { includeAll?: T } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'MIDDLE',
      type: 'SEMESTER',
    }) as T extends true
      ? (AllTypeCurriculumCoreItem | CurriculumCoreItem<'MIDDLE'>)[]
      : CurriculumCoreItem<'MIDDLE'>[]
  }

  // 고등학교 ======================================================================================================
  /** 고등학교 과목 */
  getGradeSubjectHigh<T extends boolean>(
    params: Params & { includeAll?: T } = { ...defaultParams, includeAll: true as T },
  ) {
    return this._getCurriculumArray({
      ...params,
      schoolType: 'HIGH',
      type: 'GRADE',
    }) as T extends true
      ? (AllTypeCurriculumCoreItem | CurriculumCoreItem<'HIGH'>)[]
      : CurriculumCoreItem<'HIGH'>[]
  }

  // 공통 ======================================================================================================
  getItemByTrieKey(trieKey: TrieKey) {
    if (trieKey === '') {
      console.error('trieKey is empty')
    }
    if (trieKey === 'ALL') {
      console.error('trieKey is ALL : 사용부에서 All check 필요')
    }
    return this._curriculumMap.get(trieKey)
  }
}
